import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Calculator, TrendingDown, Gauge, PiggyBank, Layers, FileCheck2, BadgeIndianRupee, PhoneCall, Trophy, Scale } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Hero } from "@/components/Hero";
import { EMICalculator } from "@/components/EMICalculator";
import { PartnerMarquee } from "@/components/PartnerMarquee";
import { Testimonials } from "@/components/Testimonials";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { InstagramStrip } from "@/components/InstagramStrip";
import { useSEO } from "@/components/SEO";
import { PRODUCTS } from "@/lib/products";
import { FALLBACK_BLOGS, FALLBACK_PARTNERS, FALLBACK_SLIDES, HOME_FAQ } from "@/lib/content";
import { fetchBlogPosts, fetchHeroSlides, fetchPartners, useWP } from "@/lib/wp";
import { formatDate } from "@/lib/format";

const WHY = [
  { icon: Layers, title: "One application, many lenders", text: "We place your profile before our partner banks and NBFCs at once — no repeating your story, no scattered hard enquiries." },
  { icon: FileCheck2, title: "Structured for approval", text: "Documents, income presentation and lender fit decide outcomes. We package your application the way credit teams like to read it." },
  { icon: BadgeIndianRupee, title: "No fee for borrowers", text: "We are compensated by lending partners. Comparing and applying through Loan Salaah costs you nothing extra." },
  { icon: PhoneCall, title: "An advisor till disbursal", text: "From the first call to the final sanction letter, one advisor owns your file and answers your phone." },
];

export default function Home() {
  const slides = useWP(fetchHeroSlides, FALLBACK_SLIDES, []);
  const partners = useWP(fetchPartners, FALLBACK_PARTNERS, []);
  const blogs = useWP(fetchBlogPosts, FALLBACK_BLOGS, []);

  useSEO({
    title: "Loan Salaah — Loans that fit your life | EMI Calculator & DSA Marketplace",
    description: "Compare personal, home, business, gold and property loans from lending partners. Calculate your EMI instantly and apply with expert guidance.",
  });

  return (
    <div data-testid="home-page">
      <Hero slides={slides} />

      {/* Products bento */}
      <section id="products" data-testid="products-section" className="relative py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="flex items-center gap-4">
              <span className="brass-rule" aria-hidden="true" />
              <span className="font-mono text-[0.65rem] uppercase tracking-[0.3em] text-brass">What we arrange</span>
            </div>
            <h2 className="mt-5 max-w-2xl font-serif text-3xl font-semibold tracking-tight text-concordia sm:text-4xl lg:text-5xl">
              Loans, a credit line and the right card — one salaah to guide you.
            </h2>
          </Reveal>

          <Stagger className="mt-14 grid gap-5 lg:grid-cols-12">
            {PRODUCTS.map((p, i) => {
              const wide = i === 0 ? "lg:col-span-7" : i === 1 ? "lg:col-span-5" : "lg:col-span-4";
              const withImage = i < 2;
              return (
                <StaggerItem key={p.slug} className={wide}>
                  <Link
                    to={`/loans/${p.slug}`}
                    data-testid={`product-card-${p.slug}`}
                    className="group relative flex h-full min-h-[15rem] flex-col justify-between overflow-hidden rounded-3xl border border-sand bg-white p-7 transition-all duration-500 hover:-translate-y-1.5 hover:border-concordia/30 hover:shadow-[0_28px_60px_rgba(30,74,61,0.14)] sm:p-8"
                  >
                    {withImage && (
                      <>
                        <img src={p.image} alt={`${p.name} — ${p.tagline}`} loading="lazy"
                          className="absolute inset-0 h-full w-full object-cover opacity-0 transition-all duration-700 group-hover:scale-105 group-hover:opacity-100" />
                        <div className="absolute inset-0 bg-gradient-to-t from-ivory via-ivory/85 to-ivory/30 opacity-0 transition-opacity duration-700 group-hover:opacity-100" aria-hidden="true" />
                      </>
                    )}
                    <div className="relative">
                      <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-concordia/[0.07] text-concordia transition-colors duration-500 group-hover:bg-concordia group-hover:text-ivory">
                        <p.icon className="h-5 w-5" />
                      </span>
                      <h3 className="mt-5 font-serif text-2xl font-semibold text-concordia">{p.name}</h3>
                      <p className="mt-2 max-w-md text-sm leading-relaxed text-slate">{p.short}</p>
                    </div>
                    <div className="relative mt-8 flex items-center justify-between">
                      <span className="font-mono text-[0.68rem] uppercase tracking-[0.2em] text-slate/80">
                        {p.rateMin > 0 ? `from ${p.rateMin}% p.a.*` : "Matched to your profile"}
                      </span>
                      <span className="flex h-9 w-9 items-center justify-center rounded-full border border-brass/40 text-brass transition-all duration-500 group-hover:border-concordia group-hover:bg-concordia group-hover:text-ivory">
                        <ArrowUpRight className="h-4 w-4 transition-transform duration-500 group-hover:rotate-45" />
                      </span>
                    </div>
                  </Link>
                </StaggerItem>
              );
            })}
          </Stagger>
          <p className="mt-6 text-xs text-slate/70">*Indicative starting rates. Final rates are set by the lending partner based on your profile.</p>
        </div>
      </section>

      {/* EMI Calculator */}
      <section data-testid="emi-section" className="relative bg-cream py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="mb-12 flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-end">
            <div>
              <div className="flex items-center gap-4">
                <span className="brass-rule" aria-hidden="true" />
                <span className="font-mono text-[0.65rem] uppercase tracking-[0.3em] text-brass">Plan before you borrow</span>
              </div>
              <h2 className="mt-5 max-w-xl font-serif text-3xl font-semibold tracking-tight text-concordia sm:text-4xl lg:text-5xl">
                Know your EMI to the last rupee.
              </h2>
            </div>
            <p className="max-w-sm text-sm leading-relaxed text-slate">
              Drag, adjust, compare. The calculator runs instantly on your device — no sign-up, no credit check, no strings.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <EMICalculator />
          </Reveal>
        </div>
      </section>

      {/* Borrower's toolkit */}
      <section data-testid="toolkit-section" className="bg-cream pb-20 sm:pb-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="mb-10">
            <div className="flex items-center gap-4">
              <span className="brass-rule" aria-hidden="true" />
              <span className="font-mono text-[0.65rem] uppercase tracking-[0.3em] text-brass">The borrower's toolkit</span>
            </div>
            <h3 className="mt-4 font-serif text-2xl font-semibold text-concordia sm:text-3xl">
              Five calculators. Zero guesswork.
            </h3>
          </Reveal>
          <Stagger className="grid gap-5 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {[
              { icon: Calculator, title: "EMI Calculator", text: "Your exact monthly outgo for any loan, rate and tenure.", to: "/emi-calculator", tid: "tool-emi" },
              { icon: Scale, title: "Compare Loans", text: "Up to 5 offers side by side — with smart tenure suggestions.", to: "/loan-comparison", tid: "tool-compare" },
              { icon: TrendingDown, title: "Savings Calculator", text: "What switching your existing loan to a lower rate really saves.", to: "/savings-calculator", tid: "tool-savings" },
              { icon: Gauge, title: "Eligibility Calculator", text: "How much you can borrow, from your income and current EMIs.", to: "/eligibility-calculator", tid: "tool-eligibility" },
              { icon: PiggyBank, title: "Prepayment Calculator", text: "How one lump sum cuts years and interest off your loan.", to: "/prepayment-calculator", tid: "tool-prepayment" },
            ].map((t) => (
              <StaggerItem key={t.to}>
                <Link
                  to={t.to}
                  data-testid={t.tid}
                  className="group flex h-full flex-col justify-between rounded-3xl border border-sand bg-white p-7 transition-all duration-500 hover:-translate-y-1.5 hover:border-concordia/30 hover:shadow-[0_28px_60px_rgba(30,74,61,0.14)]"
                >
                  <div>
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-concordia/[0.07] text-concordia transition-colors duration-500 group-hover:bg-concordia group-hover:text-ivory">
                      <t.icon className="h-5 w-5" />
                    </span>
                    <h4 className="mt-5 font-serif text-xl font-semibold text-concordia">{t.title}</h4>
                    <p className="mt-2 text-sm leading-relaxed text-slate">{t.text}</p>
                  </div>
                  <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-concordia">
                    Open
                    <ArrowRight className="h-4 w-4 text-brass transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Compare Loans showcase */}
      <section data-testid="compare-showcase" className="bg-cream pb-20 sm:pb-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="relative grid gap-10 overflow-hidden rounded-[2.5rem] bg-concordia-ink p-10 text-ivory shadow-[0_40px_90px_rgba(20,40,75,0.35)] sm:p-16 lg:grid-cols-2 lg:items-center">
              <div className="noise-overlay" aria-hidden="true" />
              <div className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-brass/20 blur-3xl" aria-hidden="true" />
              <div className="relative">
                <div className="flex items-center gap-4">
                  <span className="brass-rule" aria-hidden="true" />
                  <span className="font-mono text-[0.65rem] uppercase tracking-[0.3em] text-brass">Amortization Calculator</span>
                </div>
                <h2 className="mt-5 font-serif text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl">
                  Three bank offers walk in. One walks out cheaper.
                </h2>
                <p className="mt-4 max-w-md text-sm leading-relaxed text-ivory/70">
                  Put up to five offers side by side — EMI, total interest and the tenure tweak that saves you the
                  most. Every amount in Indian format, spelled out in words.
                </p>
                <ul className="mt-6 flex flex-wrap gap-2">
                  {["EMIs side by side", "Smart tenure suggestions", "Amounts in words"].map((chip) => (
                    <li key={chip} className="rounded-full border border-ivory/20 px-4 py-1.5 text-xs font-medium text-ivory/80">
                      {chip}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/loan-comparison"
                  data-testid="showcase-compare-button"
                  className="group mt-8 inline-flex items-center gap-2 rounded-full bg-ivory px-8 py-4 text-sm font-semibold text-concordia transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_14px_34px_rgba(0,0,0,0.35)]"
                >
                  Compare loans now
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
              <div className="relative">
                <div className="space-y-3">
                  {[
                    { name: "Bank A", rate: "9.99% p.a.", emi: "₹21,242/mo", best: false },
                    { name: "Bank B", rate: "10.50% p.a.", emi: "₹21,494/mo", best: false },
                    { name: "Bank C", rate: "9.75% p.a.", emi: "₹21,124/mo", best: true },
                  ].map((row) => (
                    <div
                      key={row.name}
                      data-testid={`showcase-card-${row.name.toLowerCase().replace(" ", "-")}`}
                      className={`flex items-center justify-between rounded-2xl border px-6 py-4 backdrop-blur-sm transition-colors ${
                        row.best ? "border-brass/60 bg-brass/10" : "border-ivory/15 bg-ivory/5"
                      }`}
                    >
                      <div>
                        <p className="font-serif text-lg font-semibold">{row.name}</p>
                        <p className="text-xs text-ivory/60">{row.rate} · ₹10,00,000 · 5y</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-champagne">{row.emi}</p>
                        {row.best && (
                          <p className="mt-1 inline-flex items-center gap-1 rounded-full bg-brass/20 px-2.5 py-0.5 text-[0.6rem] font-semibold uppercase tracking-wider text-brass" data-testid="showcase-best-chip">
                            <Trophy className="h-3 w-3" /> Lowest total cost
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <p className="mt-4 text-center text-[0.65rem] text-ivory/40">Illustrative sample — run your own numbers in the tool</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Why Loan Salaah */}
      <section data-testid="why-section" className="relative overflow-hidden bg-concordia-ink py-20 text-ivory sm:py-28">
        <div className="noise-overlay" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="flex items-center gap-4">
              <span className="brass-rule" aria-hidden="true" />
              <span className="font-mono text-[0.65rem] uppercase tracking-[0.3em] text-brass">Why Loan Salaah</span>
            </div>
            <h2 className="mt-5 max-w-2xl font-serif text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl">
              Borrowing is a craft. We practise it for you.
            </h2>
          </Reveal>
          <Stagger className="mt-14 grid gap-px overflow-hidden rounded-3xl border border-ivory/10 bg-ivory/10 sm:grid-cols-2 lg:grid-cols-4" gap={0.1}>
            {WHY.map((w) => (
              <StaggerItem key={w.title} className="bg-concordia-ink">
                <div className="group h-full p-8 transition-colors duration-500 hover:bg-concordia-deep" data-testid={`why-card-${w.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <w.icon className="h-6 w-6 text-brass" />
                  <h3 className="mt-6 font-serif text-xl font-semibold">{w.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ivory/60">{w.text}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Borrower stories */}
      <Testimonials />

      {/* Partners */}
      <section data-testid="partners-section" className="py-16 sm:py-20">
        <Reveal className="mx-auto mb-10 max-w-7xl px-4 sm:px-6 lg:px-8">
          <p className="text-center font-mono text-[0.65rem] uppercase tracking-[0.3em] text-slate">
            Offers compared across our lending partner network
          </p>
        </Reveal>
        <PartnerMarquee partners={partners} />
      </section>

      {/* Blog strip */}
      <section data-testid="blog-strip" className="bg-cream py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="flex items-end justify-between gap-6">
            <div>
              <div className="flex items-center gap-4">
                <span className="brass-rule" aria-hidden="true" />
                <span className="font-mono text-[0.65rem] uppercase tracking-[0.3em] text-brass">From the Salaah desk</span>
              </div>
              <h2 className="mt-5 font-serif text-3xl font-semibold tracking-tight text-concordia sm:text-4xl lg:text-5xl">
                Borrow smarter, in plain language.
              </h2>
            </div>
            <Link to="/blog" data-testid="blog-strip-view-all" className="group hidden items-center gap-2 text-sm font-semibold text-concordia sm:inline-flex">
              All articles
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
          <Stagger className="mt-12 grid gap-6 md:grid-cols-3">
            {blogs.slice(0, 3).map((b) => (
              <StaggerItem key={b.id || b.slug}>
                <Link to={`/blog/${b.slug}`} data-testid={`blog-card-${b.slug}`} className="group block h-full overflow-hidden rounded-3xl border border-sand bg-white transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_28px_60px_rgba(30,74,61,0.14)]">
                  {b.image && (
                    <div className="aspect-[16/10] overflow-hidden">
                      <img src={b.image} alt={b.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    </div>
                  )}
                  <div className="p-6">
                    <p className="font-mono text-[0.62rem] uppercase tracking-[0.25em] text-brass">{b.categories?.[0] || b.category || "Guides"}</p>
                    <h3 className="mt-3 font-serif text-xl font-semibold leading-snug text-concordia">{b.title}</h3>
                    <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-slate">{b.excerpt}</p>
                    <p className="mt-4 text-xs text-slate/70">{formatDate(b.date)}</p>
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Instagram */}
      <InstagramStrip />

      {/* FAQ */}
      <section data-testid="faq-section" className="py-20 sm:py-28">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
          <Reveal className="lg:col-span-5">
            <div className="flex items-center gap-4">
              <span className="brass-rule" aria-hidden="true" />
              <span className="font-mono text-[0.65rem] uppercase tracking-[0.3em] text-brass">Good questions</span>
            </div>
            <h2 className="mt-5 font-serif text-3xl font-semibold tracking-tight text-concordia sm:text-4xl">
              Asked often, answered honestly.
            </h2>
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-slate">
              Everything here is also how we work — transparent about what we are (a DSA) and what we are not (a lender).
            </p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7">
            <Accordion type="single" collapsible className="divide-y divide-sand rounded-3xl border border-sand bg-white px-6">
              {HOME_FAQ.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-none">
                  <AccordionTrigger data-testid={`faq-question-${i}`} className="py-5 text-left font-serif text-lg font-semibold text-concordia hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="pb-5 text-sm leading-relaxed text-slate">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 pb-24 sm:px-6 lg:px-8">
        <Reveal>
          <div className="relative mx-auto max-w-7xl overflow-hidden rounded-[2.5rem] bg-concordia p-10 text-ivory shadow-[0_40px_90px_rgba(30,74,61,0.35)] sm:p-16" data-testid="home-cta-banner">
            <div className="noise-overlay" aria-hidden="true" />
            <div className="pointer-events-none absolute -bottom-24 -right-24 h-80 w-80 rounded-full bg-concordia-mist/40 blur-3xl" aria-hidden="true" />
            <div className="relative flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-center">
              <div>
                <h2 className="max-w-xl font-serif text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl">
                  The right loan is a calculation away.
                </h2>
                <p className="mt-4 max-w-md text-sm leading-relaxed text-ivory/70">
                  Run your numbers, send an enquiry, and let an advisor do the legwork across our lending partners.
                </p>
              </div>
              <div className="flex flex-wrap gap-4">
                <Link to="/emi-calculator" data-testid="cta-calculator-button" className="rounded-full border border-ivory/30 px-7 py-3.5 text-sm font-semibold transition-colors hover:border-brass hover:text-brass">
                  Open the calculator
                </Link>
                <Link to="/apply" data-testid="cta-apply-button" className="rounded-full bg-ivory px-7 py-3.5 text-sm font-semibold text-concordia transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_14px_34px_rgba(0,0,0,0.3)]">
                  Apply Now
                </Link>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
